/**
 * Opens the desktop mega menu on HOVER and on KEYBOARD FOCUS, alongside — never
 * instead of — the click route the `<button popovertarget>` already provides.
 *
 * Client, 2026-09-07: *"menu should open on hover, not only on click of arrow"*.
 *
 * ── WHY THERE IS JAVASCRIPT HERE AT ALL ─────────────────────────────────────
 *
 * `snippets/rp-mega-menu.liquid` used to say, correctly, that the mega menu
 * *"ships ZERO bytes of JavaScript"*. It cannot say that and open on hover. The
 * two CSS-only routes were both tried on paper and both are worse:
 *
 *   1. `.site-nav__item:hover > .rp-mega { display: block }`. A closed popover is
 *      hidden by the UA rule `[popover]:not(:popover-open) { display: none }`,
 *      which is not `!important`, so an author `display: block` does win and the
 *      panel appears. But it appears OUTSIDE the top layer, because top-layer
 *      promotion happens when the popover is SHOWN, not when it is displayed. The
 *      panel would then be a normally-painted `position: fixed` box inside a
 *      `z-index: 18` sticky header — which mostly works, and silently stops
 *      working the day anything above it in the tree gets a transform, a filter
 *      or `contain`. `src/u10-search-menu.css` already records one measured
 *      failure of exactly that class (`position: absolute` resolving against the
 *      initial containing block and landing the panel at `top: 2113px`).
 *
 *   2. `:focus-within` for the keyboard route, per the product card's pattern.
 *      It cannot work HERE, and the reason is the click route this must not
 *      remove. A keyboard user tabs to the caret (panel opens on focus), presses
 *      Enter (the invoker TOGGLES the popover shut) — and `:focus-within` is
 *      still true, because focus never moved. The panel stays open and Enter
 *      looks broken. The product card has no such control, which is why the
 *      pattern is right there and wrong here.
 *
 * Driving `showPopover()` / `hidePopover()` has neither problem: the panel is in
 * the top layer on every route, so the geometry `src/u10-search-menu.css`
 * measured stays the geometry that ships, and outside-click light dismiss keeps
 * coming from the UA. And because the panel closes by a real state change rather
 * than by a selector no longer matching, pressing Enter after a focus-open closes
 * it and it STAYS closed — no focus event fires, so nothing re-opens it.
 *
 * ⚠️ ESCAPE IS THE ONE UA BEHAVIOUR THAT DID NOT SURVIVE, and it is handled
 * explicitly at the foot of this file rather than assumed. Measured, not
 * inferred. Do not delete that handler on the grounds that popovers close on
 * Escape by themselves.
 *
 * ── PROGRESSIVE ENHANCEMENT ────────────────────────────────────────────────
 * If this module never loads, the menu is exactly what it was: a `<button
 * popovertarget>` that click, Enter, Space and tap all operate. Nothing below is
 * required for the menu to work; it only adds two more ways in.
 *
 * ── TOUCH, AND WHERE THE CARET STILL IS ────────────────────────────────────
 * There is no hover on touch, and a `pointerenter` from a finger arrives at the
 * same moment as the tap. Opening on it would make the caret's own toggle
 * unreachable — tap to open (pointerenter), tap again to close (click), reopen
 * (pointerenter). So the pointer route is gated twice: the pointer must report
 * itself as a mouse AND the environment must claim real hover.
 *
 * That gate is the mirror of the CSS one. `src/u10-search-menu.css` renders the
 * caret only under `@media (hover: none)` — nine artboards draw this nav with no
 * disclosure glyph at all — so the two halves meet exactly: where the caret is
 * drawn, this module does not open on pointer; where this module opens on
 * pointer, the caret is not drawn. Neither surface ends up with both or neither.
 */

/* The corridor between the nav item and the panel is contiguous by construction
   — `src/app.css` stretches `.site-nav`, `.site-nav__list` and `.site-nav__item`
   to the full bar height, so the item's bottom edge and the panel's `top:
   var(--header-height)` are the same y. This grace period is for the diagonal:
   a pointer travelling from the last nav item toward the panel's left-hand
   columns can clip the outside of both boxes for a frame or two. 120ms is under
   the ~150ms at which a delay starts to read as lag, and it is not a transition
   — nothing animates, so `--motion-scale` does not apply. */
const CLOSE_GRACE_MS = 120;

/** @type {WeakMap<Element, number>} */
const closeTimers = new WeakMap();

/**
 * `showPopover()` and `hidePopover()` BOTH THROW if the popover is already in the
 * state being asked for — `check popover validity` rejects the call before it
 * does anything. That is not defensive coding, it is the specified behaviour, and
 * it is reachable here: showing item B's panel auto-closes item A's, so A's
 * pending close timer then fires against an already-closed panel.
 *
 * @param {HTMLElement} panel
 * @param {boolean} open
 */
function setOpen(panel, open) {
  const isOpen = panel.matches(':popover-open');
  if (isOpen === open) return;
  if (open) panel.showPopover();
  else panel.hidePopover();
}

/**
 * @param {Element} item
 */
function cancelClose(item) {
  const timer = closeTimers.get(item);
  if (timer !== undefined) {
    clearTimeout(timer);
    closeTimers.delete(item);
  }
}

/**
 * @param {Element} item
 * @param {HTMLElement} panel
 */
function scheduleClose(item, panel) {
  cancelClose(item);
  closeTimers.set(
    item,
    setTimeout(() => {
      closeTimers.delete(item);
      setOpen(panel, false);
    }, CLOSE_GRACE_MS)
  );
}

/**
 * @param {Element} item
 * @param {HTMLElement} panel
 */
function wire(item, panel) {
  /* ── Pointer ──────────────────────────────────────────────────────────── */
  item.addEventListener('pointerenter', (event) => {
    if (event.pointerType !== 'mouse') return;
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    cancelClose(item);
    setOpen(panel, true);
  });

  /* `pointerleave` fires only once the pointer has left the item AND every one of
     its descendants. The panel IS a descendant — the top layer changes paint
     order and containing block, never the tree — so hovering the open panel does
     not count as having left. This is the same fact `src/app.css` records about
     `white-space` inheritance reaching the panel, used deliberately. */
  item.addEventListener('pointerleave', (event) => {
    if (event.pointerType !== 'mouse') return;
    scheduleClose(item, panel);
  });

  /* ── Keyboard ─────────────────────────────────────────────────────────── */
  /* `:focus-visible`, not plain focus. A mouse click on the nav link focuses it
     without matching `:focus-visible` in any current engine, so clicking through
     to a collection does not flash the panel open on the way out; a Tab does
     match, and opens it. Same distinction `src/u3-shoppable-image.css` makes and
     for the same reason. */
  item.addEventListener('focusin', (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !target.matches(':focus-visible')) return;
    cancelClose(item);
    setOpen(panel, true);
  });

  item.addEventListener('focusout', (event) => {
    const next = event.relatedTarget;
    if (next instanceof Node && item.contains(next)) return;
    cancelClose(item);
    setOpen(panel, false);
  });
}

/* ⚠️ ESCAPE IS HANDLED HERE RATHER THAN LEFT TO THE UA, and the reason is the
   caret. An auto popover's light dismiss returns focus to its INVOKER, and on a
   hover-capable pointer `src/u10-search-menu.css` sets that invoker to
   `display: none` — the artboard draws no caret there. A dismissal whose focus
   return target is not rendered is not one a keyboard user can rely on, so the
   panel is closed explicitly and focus goes to the nav link, which is rendered
   on every surface and is the item's real anchor.

   MEASURED, and this is why the handler exists rather than being belt-and-braces:
   with the panel open from a Tab, a trusted Escape reached the document — a
   capture listener recorded `keys: ["Escape"]` — and NO `toggle` event fired.
   The UA route did not close it. It closes now.

   Document-level, not per item, so it also covers a panel opened by HOVER, where
   nothing inside the item has focus and a listener on the item would never see
   the key. `preventDefault` only when a panel was actually closed: an Escape
   that closed nothing must stay available to whatever else wants it. */
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  const panel = document.querySelector('.site-nav__item .rp-mega:popover-open');
  if (!(panel instanceof HTMLElement)) return;
  const item = panel.closest('.site-nav__item');
  event.preventDefault();
  if (item) cancelClose(item);
  setOpen(panel, false);
  const link = item && item.querySelector('.site-nav__link');
  if (link instanceof HTMLElement && item.contains(document.activeElement)) link.focus();
});

/* The header is re-rendered by the Section Rendering API on some routes, so this
   binds per element rather than once per document, and marks what it has bound.
   A second run over the same bar is a no-op instead of a second set of listeners
   racing the first. */
function init() {
  document.querySelectorAll('.site-nav__item .rp-mega').forEach((panel) => {
    const item = panel.closest('.site-nav__item');
    if (!item || item.hasAttribute('data-rp-mega-bound')) return;
    if (!(panel instanceof HTMLElement) || typeof panel.showPopover !== 'function') return;
    item.setAttribute('data-rp-mega-bound', '');
    wire(item, panel);
  });
}

init();
document.addEventListener('shopify:section:load', init);
